import type { FC } from "react";
import type { Doc } from "sharedb/lib/client";

import type { WorksheetType } from "../../../../shared/types/worksheet";
import { hasEditableBlocks } from "../../../../shared/worksheets/doc-helpers";

import { PageOverviewHeader } from "components/materials/page/content/PageOverviewHeader";
import { useNameFromPageAccessId } from "components/materials/page/helpers";
import { FeatureSimpleHeader } from "components/materials/presentation/featuring/header/FeatureSimpleHeader";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { FeatureStatus } from "utils/presentation";

type ProjectorHeaderProps = {
  doc: Doc<WorksheetType> | undefined | null;
  pageId: string;
  featureStatus: FeatureStatus;
  accessId: string | undefined | null;
  blockId: string | undefined | null;
};

export const ProjectorHeader: FC<ProjectorHeaderProps> = ({
  doc,
  pageId,
  featureStatus,
  accessId,
  blockId,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "title", "anonymize_student_responses"],
  );
  const blocks = doc?.data?.b;
  const presentedBlock = blocks?.find((b) => b.id === blockId);
  const responseName = useNameFromPageAccessId(accessId, pageId);
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const isStaticContent = !hasEditableBlocks(doc);

  if (!page) {
    return null;
  }

  return (
    <div className="projector-view-header flex w-full items-center justify-center">
      <div className="flex w-full justify-between gap-1">
        <div className="flex w-full grow items-center gap-3">
          <h1 className="my-0 text-black-tint-20">{page.title}</h1>

          {isFeaturing && (
            <FeatureSimpleHeader
              anonymizeResponses={Boolean(page.anonymize_student_responses)}
              blockType={presentedBlock?.t}
              className="gap-1 text-2xl text-black-tint-20"
              featureStatus={featureStatus}
              responseName={responseName}
              isPresenting
            />
          )}
        </div>

        <PageOverviewHeader
          className="px-0 md:px-0"
          isStaticContent={isStaticContent}
          key={pageId}
          pageId={pageId}
          isProjectorView
          readOnly
        />
      </div>
    </div>
  );
};
